import React, { useState } from "react";
import { Plus, Trash2, Save, Upload, Image as ImageIcon, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { api, ICONS, ICON_OPTIONS, mediaUrl } from "../api";
import { useApp } from "../context/AppContext";
import { MultiCombobox } from "../components/Combobox";

const SITE_IMAGE_SLOTS = [
  { key: "hero", label: "Home — Hero image" },
  { key: "handoff", label: "Home — Parcel handoff photo" },
  { key: "warehouse", label: "Warehouse photo (Home & About)" },
  { key: "servicesTruck", label: "Services — Truck photo" },
  { key: "servicesFleet", label: "Services — Fleet photo" },
  { key: "aboutTeam", label: "About — Team photo" },
  { key: "support", label: "Support — Photo" },
];

export function ImageManager() {
  const { siteImages, refreshSiteImages } = useApp();
  const [busy, setBusy] = useState(null);

  const onPick = (key) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setBusy(key);
    try {
      await api.uploadSiteImage(key, file);
      await refreshSiteImages();
      toast.success("Image updated — it's now live on the site");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed, please try again");
    } finally {
      setBusy(null);
      e.target.value = "";
    }
  };

  const onReset = (key) => async () => {
    setBusy(key);
    try { await api.resetSiteImage(key); await refreshSiteImages(); toast.success("Reset to the default image"); }
    catch { toast.error("Could not reset"); }
    finally { setBusy(null); }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-display font-bold text-2xl">Site images</h2>
        <p className="text-neutral-600 text-sm mt-1">Upload your own photos to replace the website's main pictures. Changes go live instantly for all visitors.</p>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        {SITE_IMAGE_SLOTS.map((slot) => {
          const current = siteImages[slot.key];
          const isBusy = busy === slot.key;
          return (
            <div key={slot.key} className="glass rounded-2xl p-5" data-testid={`site-image-${slot.key}`}>
              <div className="flex items-center justify-between mb-3">
                <p className="font-semibold text-sm">{slot.label}</p>
                {current && <span data-testid={`site-image-custom-${slot.key}`} className="text-[11px] font-medium text-orange-600 bg-orange-500/10 px-2 py-0.5 rounded-full">Custom</span>}
              </div>
              <div className="rounded-xl overflow-hidden bg-black/[0.04] h-40 grid place-items-center mb-4">
                {current ? <img data-testid={`site-image-preview-${slot.key}`} src={mediaUrl(current)} alt={slot.label} className="w-full h-full object-contain" />
                  : <div className="text-center text-neutral-400"><ImageIcon className="w-8 h-8 mx-auto mb-1" /><p className="text-xs">Using default image</p></div>}
              </div>
              <div className="flex gap-2">
                <label className="flex-1">
                  <input type="file" accept="image/*" className="hidden" onChange={onPick(slot.key)} data-testid={`site-image-input-${slot.key}`} disabled={isBusy} />
                  <span className={`w-full inline-flex items-center justify-center gap-1.5 h-9 rounded-lg text-sm font-medium cursor-pointer transition-opacity ${isBusy ? "opacity-60 pointer-events-none" : ""} orange-gradient text-white hover:opacity-90`}>
                    <Upload className="w-4 h-4" /> {isBusy ? "Uploading…" : "Upload new"}
                  </span>
                </label>
                {current && (
                  <Button type="button" variant="outline" onClick={onReset(slot.key)} disabled={isBusy}
                    className="border-neutral-300 text-neutral-700 hover:bg-black/[0.04] h-9 gap-1.5" data-testid={`site-image-reset-${slot.key}`}>
                    <X className="w-4 h-4" /> Reset
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const toBase64 = (file) => new Promise((res, rej) => {
  const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
});

function Field({ label, ...props }) {
  return (
    <div>
      <Label className="text-sm text-neutral-700">{label}</Label>
      <Input {...props} className="bg-black/[0.03] border-black/10 mt-1.5" />
    </div>
  );
}

// ---------------- Corridors (with price banner image upload) ----------------
export function CorridorManager() {
  const { corridors, loadContent } = useApp();
  const blank = { name: "", from: "", to: "", days: "", desc: "", tag: "", banner: "" };
  const [draft, setDraft] = useState(blank);

  const upload = (setter, obj) => async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 2.5 * 1024 * 1024) { toast.error("Please use an image under 2.5MB"); return; }
    const b64 = await toBase64(file); setter({ ...obj, banner: b64 }); toast.success("Banner ready — remember to save");
  };

  const add = async () => {
    if (!draft.name) { toast.error("Route name is required"); return; }
    await api.addCorridor(draft); await loadContent(); setDraft(blank); toast.success("Corridor added");
  };

  return (
    <div className="space-y-6">
      <div className="glass rounded-2xl p-6">
        <h3 className="font-display font-semibold text-lg flex items-center gap-2 mb-4"><Plus className="w-5 h-5 text-orange-500" /> Add corridor / route</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Route name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Europe → Sri Lanka" />
          <Field label="Estimated time" value={draft.days} onChange={(e) => setDraft({ ...draft, days: e.target.value })} placeholder="5-8 days" />
          <Field label="From (flag emoji)" value={draft.from} onChange={(e) => setDraft({ ...draft, from: e.target.value })} placeholder="🇪🇺" />
          <Field label="To (flag emoji)" value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} placeholder="🇱🇰" />
          <Field label="Tag (optional)" value={draft.tag} onChange={(e) => setDraft({ ...draft, tag: e.target.value })} placeholder="Most Popular" />
          <div>
            <Label className="text-sm text-neutral-700">Price-rate banner image</Label>
            <label className="mt-1.5 flex items-center gap-2 glass rounded-lg px-3 h-10 cursor-pointer hover:border-orange-500/50 border border-black/10">
              <Upload className="w-4 h-4 text-orange-500" /><span className="text-sm text-neutral-600">{draft.banner ? "Image selected" : "Upload image"}</span>
              <input type="file" accept="image/*" className="hidden" onChange={upload(setDraft, draft)} />
            </label>
          </div>
        </div>
        <div className="mt-4"><Label className="text-sm text-neutral-700">Description</Label><Textarea value={draft.desc} onChange={(e) => setDraft({ ...draft, desc: e.target.value })} className="bg-black/[0.03] border-black/10 mt-1.5" /></div>
        {draft.banner && <img src={draft.banner} alt="preview" className="mt-3 h-28 rounded-lg object-cover" />}
        <Button onClick={add} className="orange-gradient text-white border-0 gap-2 hover:opacity-90 mt-4"><Plus className="w-4 h-4" /> Add corridor</Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {corridors.map((c) => <CorridorRow key={c.id} c={c} upload={upload} reload={loadContent} />)}
      </div>
    </div>
  );
}

function CorridorRow({ c, upload, reload }) {
  const [e, setE] = useState(c);
  const save = async () => { await api.updateCorridor(c.id, e); await reload(); toast.success("Corridor updated"); };
  const del = async () => { await api.deleteCorridor(c.id); await reload(); toast.success("Corridor deleted"); };
  return (
    <div className="glass rounded-2xl p-5">
      {e.banner && <img src={e.banner} alt="" className="h-28 w-full rounded-lg object-cover mb-3" />}
      <div className="grid grid-cols-2 gap-3">
        <Input value={e.name} onChange={(ev) => setE({ ...e, name: ev.target.value })} className="bg-black/[0.03] border-black/10 col-span-2" placeholder="Route name" />
        <Input value={e.from} onChange={(ev) => setE({ ...e, from: ev.target.value })} className="bg-black/[0.03] border-black/10" placeholder="From 🇪🇺" />
        <Input value={e.to} onChange={(ev) => setE({ ...e, to: ev.target.value })} className="bg-black/[0.03] border-black/10" placeholder="To 🇱🇰" />
        <Input value={e.days} onChange={(ev) => setE({ ...e, days: ev.target.value })} className="bg-black/[0.03] border-black/10" placeholder="Days" />
        <Input value={e.tag} onChange={(ev) => setE({ ...e, tag: ev.target.value })} className="bg-black/[0.03] border-black/10" placeholder="Tag" />
      </div>
      <Textarea value={e.desc} onChange={(ev) => setE({ ...e, desc: ev.target.value })} className="bg-black/[0.03] border-black/10 mt-3 min-h-16" />
      <div className="flex flex-wrap gap-2 mt-3">
        <label className="flex items-center gap-2 glass rounded-lg px-3 h-9 cursor-pointer hover:border-orange-500/50 border border-black/10 text-sm">
          <ImageIcon className="w-4 h-4 text-orange-500" /> {e.banner ? "Change rate image" : "Upload rate image"}
          <input type="file" accept="image/*" className="hidden" onChange={upload(setE, e)} />
        </label>
        {e.banner && <Button variant="outline" className="border-neutral-300 h-9 gap-1.5" onClick={() => setE({ ...e, banner: "" })}><X className="w-4 h-4" /> Remove image</Button>}
        <Button onClick={save} className="orange-gradient text-white border-0 gap-1.5 h-9 hover:opacity-90"><Save className="w-4 h-4" /> Save</Button>
        <Button variant="ghost" className="text-red-400 hover:text-red-300 h-9" onClick={del}><Trash2 className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

// ---------------- Services ----------------
export function ServiceManager() {
  const { services, loadContent } = useApp();
  const [d, setD] = useState({ icon: "package-check", title: "", desc: "" });
  const add = async () => { if (!d.title) { toast.error("Title required"); return; } await api.addService(d); await loadContent(); setD({ icon: "package-check", title: "", desc: "" }); toast.success("Service added"); };
  return (
    <div className="space-y-6">
      <div className="glass rounded-2xl p-6 grid md:grid-cols-4 gap-3 items-end">
        <div>
          <Label className="text-sm text-neutral-700">Icon</Label>
          <Select value={d.icon} onValueChange={(v) => setD({ ...d, icon: v })}><SelectTrigger className="bg-black/[0.03] border-black/10 mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>{ICON_OPTIONS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent></Select>
        </div>
        <Field label="Title" value={d.title} onChange={(e) => setD({ ...d, title: e.target.value })} />
        <Field label="Description" value={d.desc} onChange={(e) => setD({ ...d, desc: e.target.value })} />
        <Button onClick={add} className="orange-gradient text-white border-0 gap-2 hover:opacity-90"><Plus className="w-4 h-4" /> Add</Button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {services.map((s) => <ServiceRow key={s.id} s={s} reload={loadContent} />)}
      </div>
    </div>
  );
}
function ServiceRow({ s, reload }) {
  const [e, setE] = useState(s);
  const Icon = ICONS[e.icon] || ICONS["package-check"];
  const save = async () => { await api.updateService(s.id, e); await reload(); toast.success("Saved"); };
  const del = async () => { await api.deleteService(s.id); await reload(); toast.success("Deleted"); };
  return (
    <div className="glass rounded-2xl p-5">
      <div className="flex items-center gap-3 mb-3"><div className="w-10 h-10 rounded-lg orange-gradient grid place-items-center"><Icon className="w-5 h-5 text-white" /></div>
        <Select value={e.icon} onValueChange={(v) => setE({ ...e, icon: v })}><SelectTrigger className="bg-black/[0.03] border-black/10 h-9 w-40"><SelectValue /></SelectTrigger>
          <SelectContent>{ICON_OPTIONS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent></Select>
      </div>
      <Input value={e.title} onChange={(ev) => setE({ ...e, title: ev.target.value })} className="bg-black/[0.03] border-black/10 mb-2" />
      <Textarea value={e.desc} onChange={(ev) => setE({ ...e, desc: ev.target.value })} className="bg-black/[0.03] border-black/10 min-h-16" />
      <div className="flex gap-2 mt-3"><Button onClick={save} className="orange-gradient text-white border-0 gap-1.5 h-9 hover:opacity-90"><Save className="w-4 h-4" /> Save</Button>
        <Button variant="ghost" className="text-red-400 hover:text-red-300 h-9" onClick={del}><Trash2 className="w-4 h-4" /></Button></div>
    </div>
  );
}

// ---------------- FAQs ----------------
export function FaqManager() {
  const { faqs, loadContent } = useApp();
  const [d, setD] = useState({ q: "", a: "" });
  const add = async () => { if (!d.q) { toast.error("Question required"); return; } await api.addFaq(d); await loadContent(); setD({ q: "", a: "" }); toast.success("FAQ added"); };
  return (
    <div className="space-y-6">
      <div className="glass rounded-2xl p-6 space-y-3">
        <Field label="Question" value={d.q} onChange={(e) => setD({ ...d, q: e.target.value })} />
        <div><Label className="text-sm text-neutral-700">Answer</Label><Textarea value={d.a} onChange={(e) => setD({ ...d, a: e.target.value })} className="bg-black/[0.03] border-black/10 mt-1.5" /></div>
        <Button onClick={add} className="orange-gradient text-white border-0 gap-2 hover:opacity-90"><Plus className="w-4 h-4" /> Add FAQ</Button>
      </div>
      <div className="space-y-4">{faqs.map((f) => <FaqRow key={f.id} f={f} reload={loadContent} />)}</div>
    </div>
  );
}
function FaqRow({ f, reload }) {
  const [e, setE] = useState(f);
  const save = async () => { await api.updateFaq(f.id, e); await reload(); toast.success("Saved"); };
  const del = async () => { await api.deleteFaq(f.id); await reload(); toast.success("Deleted"); };
  return (
    <div className="glass rounded-2xl p-5">
      <Input value={e.q} onChange={(ev) => setE({ ...e, q: ev.target.value })} className="bg-black/[0.03] border-black/10 mb-2 font-medium" />
      <Textarea value={e.a} onChange={(ev) => setE({ ...e, a: ev.target.value })} className="bg-black/[0.03] border-black/10 min-h-16" />
      <div className="flex gap-2 mt-3"><Button onClick={save} className="orange-gradient text-white border-0 gap-1.5 h-9 hover:opacity-90"><Save className="w-4 h-4" /> Save</Button>
        <Button variant="ghost" className="text-red-400 hover:text-red-300 h-9" onClick={del}><Trash2 className="w-4 h-4" /></Button></div>
    </div>
  );
}

// ---------------- Countries ----------------
export function CountryManager() {
  const { countries, loadContent } = useApp();
  const [d, setD] = useState({ name: "", flag: "" });
  const add = async () => { if (!d.name) { toast.error("Country name required"); return; } await api.addCountry(d); await loadContent(); setD({ name: "", flag: "" }); toast.success("Country added"); };
  const del = async (id) => { await api.deleteCountry(id); await loadContent(); toast.success("Removed"); };
  return (
    <div className="space-y-6">
      <div className="glass rounded-2xl p-6 grid sm:grid-cols-3 gap-3 items-end">
        <Field label="Country name" value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} placeholder="Italy" />
        <Field label="Flag emoji" value={d.flag} onChange={(e) => setD({ ...d, flag: e.target.value })} placeholder="🇮🇹" />
        <Button onClick={add} className="orange-gradient text-white border-0 gap-2 hover:opacity-90"><Plus className="w-4 h-4" /> Add country</Button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {countries.map((c) => (
          <div key={c.id} className="glass rounded-xl p-4 flex items-center justify-between">
            <span className="flex items-center gap-2 text-sm"><span className="text-2xl">{c.flag}</span> {c.name}</span>
            <Button variant="ghost" size="icon" className="text-neutral-500 hover:text-red-400 h-8 w-8" onClick={() => del(c.id)}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}


// ---------------- Warehouses ----------------
export function WarehouseManager() {
  const { warehouses, loadContent } = useApp();
  const [d, setD] = useState({ city: "", country: "", flag: "" });
  const add = async () => { if (!d.city) { toast.error("City required"); return; } await api.addWarehouse(d); await loadContent(); setD({ city: "", country: "", flag: "" }); toast.success("Warehouse added"); };
  const del = async (id) => { await api.deleteWarehouse(id); await loadContent(); toast.success("Removed"); };
  return (
    <div className="space-y-6">
      <div className="glass rounded-2xl p-6 grid sm:grid-cols-4 gap-3 items-end">
        <Field label="City" value={d.city} onChange={(e) => setD({ ...d, city: e.target.value })} placeholder="Milan" />
        <Field label="Country" value={d.country} onChange={(e) => setD({ ...d, country: e.target.value })} placeholder="Italy" />
        <Field label="Flag emoji" value={d.flag} onChange={(e) => setD({ ...d, flag: e.target.value })} placeholder="🇮🇹" />
        <Button onClick={add} className="orange-gradient text-white border-0 gap-2 hover:opacity-90"><Plus className="w-4 h-4" /> Add warehouse</Button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {warehouses.map((w) => (
          <div key={w.id} className="glass rounded-xl p-4 flex items-center justify-between">
            <span className="flex items-center gap-2 text-sm"><span className="text-2xl">{w.flag}</span> <span><b className="font-medium">{w.city}</b><br /><span className="text-xs text-neutral-500">{w.country}</span></span></span>
            <Button variant="ghost" size="icon" className="text-neutral-500 hover:text-red-400 h-8 w-8" onClick={() => del(w.id)}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------- Employees (with WhatsApp + assigned countries) ----------------
export function EmployeeManager() {
  const { countries, loadContent } = useApp();
  const [employees, setEmployees] = useState([]);
  const blank = { name: "", email: "", password: "", whatsapp: "", countries: [] };
  const [d, setD] = useState(blank);
  const options = countries.map((c) => ({ value: c.name, label: c.name, flag: c.flag }));

  const load = () => api.getEmployees().then(setEmployees).catch(() => {});
  React.useEffect(() => { load(); }, []);

  const add = async () => {
    if (!d.name || !d.email || !d.password) { toast.error("Name, email & password required"); return; }
    try { await api.addEmployee(d); setD(blank); await load(); toast.success("Employee added"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
  };
  const del = async (id) => { await api.deleteEmployee(id); await load(); toast.success("Employee removed"); };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="glass rounded-2xl p-6 space-y-4 h-fit">
        <h3 className="font-display font-semibold text-lg flex items-center gap-2"><Plus className="w-5 h-5 text-orange-500" /> Add employee</h3>
        <p data-testid="employee-inquiry-access-info" className="text-sm text-neutral-600">All employees have access to customer inquiries and replies. Parcel and tracking views remain country-based.</p>
        <Field data-testid="employee-name-input" label="Name" value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} placeholder="Employee name" />
        <Field data-testid="employee-email-input" label="Email" value={d.email} onChange={(e) => setD({ ...d, email: e.target.value })} placeholder="employee@smartlogistics.eu" />
        <Field data-testid="employee-password-input" type="password" label="Password" value={d.password} onChange={(e) => setD({ ...d, password: e.target.value })} placeholder="Set a password" />
        <Field data-testid="employee-whatsapp-input" label="WhatsApp number (digits only)" value={d.whatsapp} onChange={(e) => setD({ ...d, whatsapp: e.target.value })} placeholder="393279890838" />
        <div>
          <Label className="text-sm text-neutral-700">Countries handled</Label>
          <div className="mt-1.5"><MultiCombobox options={options} values={d.countries} onChange={(v) => setD({ ...d, countries: v })} /></div>
        </div>
        <Button data-testid="employee-add-button" onClick={add} className="orange-gradient text-white border-0 gap-2 hover:opacity-90"><Plus className="w-4 h-4" /> Add employee</Button>
      </div>

      <div className="space-y-4">
        <h3 className="font-display font-semibold text-lg">Team ({employees.length})</h3>
        {employees.length ? employees.map((u) => <EmployeeRow key={u.id} u={u} options={options} reload={load} del={del} />)
          : <div className="glass rounded-2xl p-8 text-center text-sm text-neutral-500">No employees yet.</div>}
      </div>
    </div>
  );
}

function EmployeeRow({ u, options, reload, del }) {
  const [e, setE] = useState({ ...u, password: "", countries: u.countries || [], whatsapp: u.whatsapp || "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.updateEmployee(u.id, e); await reload(); toast.success("Employee updated"); }
    catch { toast.error("Could not save employee permissions. Please try again."); }
    finally { setSaving(false); }
  };
  return (
    <div data-testid={`employee-row-${u.id}`} className="glass rounded-2xl p-5 min-w-0">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full orange-gradient grid place-items-center text-white text-sm font-bold">{(e.name || "?").charAt(0)}</div>
          <div className="min-w-0"><p data-testid={`employee-name-${u.id}`} className="text-sm font-medium break-words">{e.name}</p><p data-testid={`employee-email-${u.id}`} className="text-xs text-neutral-500 break-all">{e.email}</p></div>
        </div>
        <Button data-testid={`employee-delete-${u.id}`} aria-label="Remove employee" variant="ghost" size="icon" className="text-neutral-500 hover:text-red-400 shrink-0" onClick={() => del(u.id)}><Trash2 className="w-4 h-4" /></Button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label className="text-xs text-neutral-600">WhatsApp number</Label><Input data-testid={`employee-whatsapp-${u.id}`} value={e.whatsapp} onChange={(ev) => setE({ ...e, whatsapp: ev.target.value })} className="bg-black/[0.03] border-black/10 mt-1" placeholder="Digits only" /></div>
        <div><Label className="text-xs text-neutral-600">New password (optional)</Label><Input data-testid={`employee-password-${u.id}`} type="password" value={e.password} onChange={(ev) => setE({ ...e, password: ev.target.value })} className="bg-black/[0.03] border-black/10 mt-1" placeholder="Leave blank to keep" /></div>
      </div>
      <div className="mt-3"><Label className="text-xs text-neutral-600">Countries handled</Label>
        <div className="mt-1"><MultiCombobox options={options} values={e.countries} onChange={(v) => setE({ ...e, countries: v })} /></div>
      </div>
      <p data-testid={`employee-inquiry-access-${u.id}`} className="text-xs text-green-700 mt-4">Inquiry access included</p>
      <Button data-testid={`employee-save-${u.id}`} disabled={saving} onClick={save} className="orange-gradient text-white border-0 gap-1.5 h-9 hover:opacity-90 mt-3"><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}</Button>
    </div>
  );
}

// ---------------- Offers (with optional banner upload) ----------------
export function OfferManager() {
  const { offers, loadContent } = useApp();
  const blank = { title: "", desc: "", tag: "", banner: "", active: true };
  const [d, setD] = useState(blank);
  const upload = (setter, obj) => async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 2.5 * 1024 * 1024) { toast.error("Use an image under 2.5MB"); return; }
    const b64 = await toBase64(file); setter({ ...obj, banner: b64 }); toast.success("Image ready — remember to save");
  };
  const add = async () => { if (!d.title) { toast.error("Title required"); return; } await api.addOffer(d); await loadContent(); setD(blank); toast.success("Offer added"); };
  return (
    <div className="space-y-6">
      <div className="glass rounded-2xl p-6 space-y-3">
        <div className="grid md:grid-cols-3 gap-3">
          <Field label="Title" value={d.title} onChange={(e) => setD({ ...d, title: e.target.value })} placeholder="New Customer Welcome" />
          <Field label="Tag / badge" value={d.tag} onChange={(e) => setD({ ...d, tag: e.target.value })} placeholder="10% OFF" />
          <div>
            <Label className="text-sm text-neutral-700">Banner image (optional)</Label>
            <label className="mt-1.5 flex items-center gap-2 glass rounded-lg px-3 h-10 cursor-pointer hover:border-orange-500/50 border border-black/10">
              <Upload className="w-4 h-4 text-orange-500" /><span className="text-sm text-neutral-600">{d.banner ? "Image selected" : "Upload"}</span>
              <input type="file" accept="image/*" className="hidden" onChange={upload(setD, d)} />
            </label>
          </div>
        </div>
        <div><Label className="text-sm text-neutral-700">Description</Label><Textarea value={d.desc} onChange={(e) => setD({ ...d, desc: e.target.value })} className="bg-black/[0.03] border-black/10 mt-1.5" /></div>
        <Button onClick={add} className="orange-gradient text-white border-0 gap-2 hover:opacity-90"><Plus className="w-4 h-4" /> Add offer</Button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">{offers.map((o) => <OfferRow key={o.id} o={o} upload={upload} reload={loadContent} />)}</div>
    </div>
  );
}
function OfferRow({ o, upload, reload }) {
  const [e, setE] = useState(o);
  const save = async () => { await api.updateOffer(o.id, e); await reload(); toast.success("Saved"); };
  const del = async () => { await api.deleteOffer(o.id); await reload(); toast.success("Deleted"); };
  return (
    <div className="glass rounded-2xl p-5">
      {e.banner && <img src={e.banner} alt="" className="h-28 w-full rounded-lg object-cover mb-3" />}
      <div className="grid grid-cols-2 gap-2">
        <Input value={e.title} onChange={(ev) => setE({ ...e, title: ev.target.value })} className="bg-black/[0.03] border-black/10 col-span-2" placeholder="Title" />
        <Input value={e.tag} onChange={(ev) => setE({ ...e, tag: ev.target.value })} className="bg-black/[0.03] border-black/10" placeholder="Tag" />
        <label className="flex items-center gap-2 glass rounded-lg px-3 h-10 cursor-pointer hover:border-orange-500/50 border border-black/10 text-sm">
          <ImageIcon className="w-4 h-4 text-orange-500" /> {e.banner ? "Change image" : "Upload image"}
          <input type="file" accept="image/*" className="hidden" onChange={upload(setE, e)} />
        </label>
      </div>
      <Textarea value={e.desc} onChange={(ev) => setE({ ...e, desc: ev.target.value })} className="bg-black/[0.03] border-black/10 mt-2 min-h-16" />
      <div className="flex items-center gap-3 mt-3">
        <label className="flex items-center gap-2 text-sm text-neutral-700"><input type="checkbox" checked={e.active !== false} onChange={(ev) => setE({ ...e, active: ev.target.checked })} /> Active</label>
        <Button onClick={save} className="orange-gradient text-white border-0 gap-1.5 h-9 hover:opacity-90"><Save className="w-4 h-4" /> Save</Button>
        <Button variant="ghost" className="text-red-500 hover:text-red-400 h-9" onClick={del}><Trash2 className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

// ---------------- Insurance packages ----------------
export function InsuranceManager() {
  const { insurance, loadContent } = useApp();
  const blank = { name: "", coverage: "", price: "", desc: "" };
  const [d, setD] = useState(blank);
  const add = async () => { if (!d.name) { toast.error("Name required"); return; } await api.addInsurance(d); await loadContent(); setD(blank); toast.success("Insurance package added"); };
  return (
    <div className="space-y-6">
      <div className="glass rounded-2xl p-6 space-y-3">
        <div className="grid md:grid-cols-3 gap-3">
          <Field label="Package name" value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} placeholder="Standard" />
          <Field label="Coverage" value={d.coverage} onChange={(e) => setD({ ...d, coverage: e.target.value })} placeholder="Up to €500" />
          <Field label="Price" value={d.price} onChange={(e) => setD({ ...d, price: e.target.value })} placeholder="€9.99" />
        </div>
        <div><Label className="text-sm text-neutral-700">Description</Label><Textarea value={d.desc} onChange={(e) => setD({ ...d, desc: e.target.value })} className="bg-black/[0.03] border-black/10 mt-1.5" /></div>
        <Button onClick={add} className="orange-gradient text-white border-0 gap-2 hover:opacity-90"><Plus className="w-4 h-4" /> Add package</Button>
      </div>
      <div className="grid md:grid-cols-3 gap-4">{insurance.map((o) => <InsuranceRow key={o.id} o={o} reload={loadContent} />)}</div>
    </div>
  );
}
function InsuranceRow({ o, reload }) {
  const [e, setE] = useState(o);
  const save = async () => { await api.updateInsurance(o.id, e); await reload(); toast.success("Saved"); };
  const del = async () => { await api.deleteInsurance(o.id); await reload(); toast.success("Deleted"); };
  return (
    <div className="glass rounded-2xl p-5 space-y-2">
      <Input value={e.name} onChange={(ev) => setE({ ...e, name: ev.target.value })} className="bg-black/[0.03] border-black/10 font-semibold" placeholder="Name" />
      <div className="grid grid-cols-2 gap-2">
        <Input value={e.coverage} onChange={(ev) => setE({ ...e, coverage: ev.target.value })} className="bg-black/[0.03] border-black/10" placeholder="Coverage" />
        <Input value={e.price} onChange={(ev) => setE({ ...e, price: ev.target.value })} className="bg-black/[0.03] border-black/10" placeholder="Price" />
      </div>
      <Textarea value={e.desc} onChange={(ev) => setE({ ...e, desc: ev.target.value })} className="bg-black/[0.03] border-black/10 min-h-16" />
      <div className="flex gap-2"><Button onClick={save} className="orange-gradient text-white border-0 gap-1.5 h-9 hover:opacity-90"><Save className="w-4 h-4" /> Save</Button>
        <Button variant="ghost" className="text-red-500 hover:text-red-400 h-9" onClick={del}><Trash2 className="w-4 h-4" /></Button></div>
    </div>
  );
}


// ---------------- Customer-service numbers ----------------
export function ContactManager() {
  const { contacts, loadContent } = useApp();
  const blank = { label: "", whatsapp: "", phone: "" };
  const [d, setD] = useState(blank);
  const add = async () => { if (!d.label || !d.whatsapp) { toast.error("Label & WhatsApp number required"); return; } await api.addContact(d); await loadContent(); setD(blank); toast.success("Number added"); };
  return (
    <div className="space-y-6">
      <div className="glass rounded-2xl p-6 grid md:grid-cols-4 gap-3 items-end">
        <Field label="Label" value={d.label} onChange={(e) => setD({ ...d, label: e.target.value })} placeholder="General Support" />
        <Field label="WhatsApp (digits only)" value={d.whatsapp} onChange={(e) => setD({ ...d, whatsapp: e.target.value })} placeholder="393279890838" />
        <Field label="Display phone" value={d.phone} onChange={(e) => setD({ ...d, phone: e.target.value })} placeholder="+39 327 989 0838" />
        <Button onClick={add} className="orange-gradient text-white border-0 gap-2 hover:opacity-90"><Plus className="w-4 h-4" /> Add number</Button>
      </div>
      <p className="text-sm text-neutral-600">Additional customer-service contacts.</p>
      <div className="grid md:grid-cols-2 gap-4">{contacts.map((c) => <ContactRow key={c.id} c={c} reload={loadContent} />)}</div>
    </div>
  );
}
function ContactRow({ c, reload }) {
  const [e, setE] = useState(c);
  const save = async () => { await api.updateContact(c.id, e); await reload(); toast.success("Saved"); };
  const del = async () => { await api.deleteContact(c.id); await reload(); toast.success("Deleted"); };
  return (
    <div className="glass rounded-2xl p-5 space-y-2">
      <Input value={e.label} onChange={(ev) => setE({ ...e, label: ev.target.value })} className="bg-black/[0.03] border-black/10 font-semibold" placeholder="Label" />
      <div className="grid grid-cols-2 gap-2">
        <Input value={e.whatsapp} onChange={(ev) => setE({ ...e, whatsapp: ev.target.value })} className="bg-black/[0.03] border-black/10" placeholder="WhatsApp digits" />
        <Input value={e.phone} onChange={(ev) => setE({ ...e, phone: ev.target.value })} className="bg-black/[0.03] border-black/10" placeholder="Display phone" />
      </div>
      <div className="flex gap-2"><Button onClick={save} className="orange-gradient text-white border-0 gap-1.5 h-9 hover:opacity-90"><Save className="w-4 h-4" /> Save</Button>
        <Button variant="ghost" className="text-red-500 hover:text-red-400 h-9" onClick={del}><Trash2 className="w-4 h-4" /></Button></div>
    </div>
  );
}

